import { withTimeout } from './withTimeout';

type RetryWithDelaysOptions = {
  isCancelled?: () => boolean;
  onAttemptError?: (attemptIndex: number, error: unknown) => void;
  beforeAttempt?: (attemptIndex: number) => Promise<void>;
  /** If set, each attempt (beforeAttempt + fn) is raced against this timeout. */
  attemptTimeoutMs?: number;
};

/**
 * Runs `fn` once per delay entry. `delaysMs[0]` is the wait before the first try.
 */
export async function retryWithDelays<T>(
  fn: () => Promise<T>,
  delaysMs: readonly number[],
  options?: RetryWithDelaysOptions
): Promise<T> {
  let lastErr: unknown = new Error('retryWithDelays: no attempts');
  for (let i = 0; i < delaysMs.length; i++) {
    if (options?.isCancelled?.()) {
      throw lastErr;
    }
    if (delaysMs[i] > 0) {
      await new Promise((r) => setTimeout(r, delaysMs[i]));
    }
    if (options?.isCancelled?.()) {
      throw lastErr;
    }
    try {
      const runAttempt = async () => {
        if (options?.beforeAttempt) {
          await options.beforeAttempt(i);
        }
        const timeoutMs = options?.attemptTimeoutMs;
        return timeoutMs && timeoutMs > 0
          ? withTimeout(fn(), timeoutMs, `retryWithDelays attempt ${i + 1} timed out`)
          : fn();
      };
      return await runAttempt();
    } catch (e) {
      lastErr = e;
      options?.onAttemptError?.(i, e);
    }
  }
  throw lastErr;
}
